<script setup lang="ts">
/**
 * App.vue - Root Component
 *
 * Responsibilities:
 * - App shell layout
 * - Theme provider
 * - Toast notifications
 */

import { onMounted, onUnmounted, provide } from 'vue'
import { RouterView } from 'vue-router'
import { useToast } from 'primevue/usetoast'

import Toast from '@/volt/Toast.vue'
import ConfirmDialog from '@/volt/ConfirmDialog.vue'

import InstallBanner from '@/components/pwa/InstallBanner.vue'
import UpdateBanner from '@/components/pwa/UpdateBanner.vue'

import ProgressSpinner from 'primevue/progressspinner'

import { useTheme } from '@/composables/useTheme'
import { useOffline } from '@/composables/useOffline'
import { useAppUpdate } from '@/composables/useAppUpdate'
import { useAutoSync } from '@/composables/useAutoSync'
import { useNotificationNavigation } from '@/composables/useNotificationNavigation'
import { useTutorials } from '@/composables/useTutorials'
import { useAuthStore } from '@/stores/auth.store'
import { usePhotosStore } from '@/stores/photos.store'
import { db } from '@/db'

// Initialize Theme
const { isDark } = useTheme()

// Offline Detection
const { isOnline } = useOffline()

// PWA Update
const { needRefresh, applyUpdate } = useAppUpdate()

// Auto-Sync on reconnect + periodic retry
useAutoSync()

// Weiche Navigation bei Notification-Klick (Service Worker -> vue-router)
useNotificationNavigation()

// Tutorial transition state (loading spinner between page switches)
const { isTransitioning } = useTutorials()

// Provide Database Instance
provide('db', db)

// Toast for offline chunk load errors (router dispatches 'chunk-load-error-offline')
const toast = useToast()
function onChunkLoadErrorOffline() {
  toast.add({
    severity: 'warn',
    summary: 'Seite nicht verfügbar',
    detail: 'Diese Seite wurde noch nicht geladen und ist offline nicht erreichbar.',
    life: 16000,
  })
}
onMounted(() => {
  window.addEventListener('chunk-load-error-offline', onChunkLoadErrorOffline)
})
onUnmounted(() => {
  window.removeEventListener('chunk-load-error-offline', onChunkLoadErrorOffline)
})

// App Initialization
onMounted(async () => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(
      '[App] TusCourses PWA initialized | Dark:',
      isDark.value,
      '| Online:',
      isOnline.value
    )
  }

  // Initialize photos store pending uploads from IndexedDB
  const photosStore = usePhotosStore()
  await photosStore.initPendingUploads()

  // One-time migration: localStorage avatar → backend
  // Only remove localStorage AFTER confirmed backend upload (online only).
  const authStore = useAuthStore()
  const photoPersonId = authStore.personId ?? authStore.userId
  if (photoPersonId && navigator.onLine) {
    const key = `tuscourses_avatar_${authStore.userId}`
    const storedAvatar = localStorage.getItem(key)
    if (storedAvatar) {
      try {
        await photosStore.uploadPhoto(photoPersonId, storedAvatar)
        // Upload succeeded — safe to remove localStorage
        localStorage.removeItem(key)
      } catch {
        // Will retry on next online app start
      }
    }
  }
})
</script>

<template>
  <div
    class="app-container min-h-screen bg-surface-50 dark:bg-surface-900 text-surface-900 dark:text-surface-0 transition-colors duration-200"
    :class="{ dark: isDark }"
  >
    <!-- PWA Update Banner (new version available) -->
    <UpdateBanner
      v-if="needRefresh"
      :on-update="applyUpdate"
    />

    <!-- PWA Install Banner -->
    <InstallBanner />

    <!-- Toast Notifications -->
    <Toast position="top-center" />

    <!-- Confirm Dialog -->
    <ConfirmDialog />

    <!-- Tutorial Cross-Batch Loading Overlay -->
    <Teleport to="body">
      <div
        v-if="isTransitioning"
        class="tus-tutorial-loading"
      >
        <ProgressSpinner
          :stroke-width="4"
          animation-duration="0.8s"
        />
      </div>
    </Teleport>

    <!-- Main Content -->
    <RouterView v-slot="{ Component, route }">
      <component
        :is="Component"
        :key="route.path"
      />
    </RouterView>
  </div>
</template>

<style>
/* Safe Area for Mobile */
.app-container {
  padding-top: env(safe-area-inset-top);
}

/* Prevent pull-to-refresh on mobile when not at top */
body {
  overscroll-behavior-y: contain;
}
</style>
